import { useState, KeyboardEvent, useEffect } from 'react'
import { Close, Help } from '@icon-park/react'
import { Icon } from '../Icon/Icon.component'
import { Tooltip } from '../Tooltip/Tooltip.component'
import { BooleanTooltipContent } from '../Tooltip/BooleanTooltip'
import { Tokens } from '@/assets/tokens'
import { TagOption, TagValidationConfig } from './HorizontalTagInput.hooks'
import {
  Container,
  Label,
  LabelRow,
  InputRow,
  TextInput,
  AddButton,
  TagsContainer,
  TagItem,
  TagText,
  RemoveButton,
  EmptyState,
  ClearAllButton,
  InfoRow,
} from './HorizontalTagInput.styles'

interface Props {
  label?: string
  placeholder?: string
  maxCharacters?: number
  preSelectedTags?: TagOption[]
  onChange: (tags: TagOption[]) => void
  disabled?: boolean
  validationConfig?: TagValidationConfig
  showClearButton?: boolean
  showBooleanHelp?: boolean
  /** Só para campos avaliados pelo matcher de termos de marca do ETL. */
  exactQuotes?: boolean
}

export function HorizontalTagInput({
  label,
  placeholder = 'Digite e pressione Enter ou clique em Adicionar',
  preSelectedTags = [],
  onChange,
  disabled = false,
  validationConfig,
  showClearButton = false,
  showBooleanHelp = false,
  exactQuotes = false,
}: Props): JSX.Element {
  const [inputValue, setInputValue] = useState('')
  const [tags, setTags] = useState<TagOption[]>(preSelectedTags)

  useEffect(() => {
    setTags(preSelectedTags)
  }, [preSelectedTags])

  function generateId(): string {
    return Math.random().toString(36).substr(2, 9)
  }

  function processTags(input: string): void {
    const trimmedValue = input.trim()

    if (!trimmedValue) return

    const terms = trimmedValue
      .split(';')
      .map((term) => term.trim())
      .filter((term) => term.length > 0)

    if (terms.length === 0) return

    const newTags: TagOption[] = terms.map((term) => ({
      id: generateId(),
      text: term,
    }))

    let validatedTags = newTags
    if (validationConfig?.customValidator) {
      validatedTags = validationConfig.customValidator(newTags, tags)
    } else {
      const allowDuplicates = validationConfig?.allowDuplicates ?? false

      validatedTags = newTags.filter((tag) => {
        const trimmedText = tag.text.trim()
        if (trimmedText.length === 0) {
          return false
        }

        if (!allowDuplicates) {
          const existingTexts = tags.map((t) => t.text.toLowerCase().trim())
          if (existingTexts.includes(trimmedText.toLowerCase())) {
            return false
          }
        }

        return true
      })

      if (!allowDuplicates) {
        validatedTags = validatedTags.filter(
          (tag, index, arr) =>
            arr.findIndex((t) => t.text.toLowerCase().trim() === tag.text.toLowerCase().trim()) === index,
        )
      }
    }

    if (validatedTags.length > 0) {
      const updatedTags = [...tags, ...validatedTags]
      setTags(updatedTags)
      setInputValue('')
      setTimeout(() => onChange(updatedTags), 0)
    }
  }

  function addTag(): void {
    processTags(inputValue)
  }

  function removeTag(tagId: string): void {
    const updatedTags = tags.filter((tag) => tag.id !== tagId)
    setTags(updatedTags)
    setTimeout(() => onChange(updatedTags), 0)
  }

  function handleKeyPress(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      addTag()
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value
    setInputValue(value)
    if (value.includes(';')) {
      processTags(value)
    }
  }

  function handleClearAll(): void {
    setTags([])
    setInputValue('')
    setTimeout(() => onChange([]), 0)
  }

  const canAdd = inputValue.trim().length > 0 && !disabled

  return (
    <Container>
      {label && (
        <LabelRow>
          <Label>{label}</Label>
          {showBooleanHelp && (
            <Tooltip value={<BooleanTooltipContent exactQuotes={exactQuotes} />}>
              <span style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                <Icon size={16} value={Help} color={Tokens.ColorUIWarnDark} />
              </span>
            </Tooltip>
          )}
        </LabelRow>
      )}

      <InputRow>
        <TextInput
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
        />
        <AddButton type="button" onClick={addTag} disabled={!canAdd}>
          Adicionar
        </AddButton>
      </InputRow>

      {showClearButton && tags.length > 0 && !disabled && (
        <InfoRow>
          <ClearAllButton type="button" onClick={handleClearAll}>
            Limpar tudo
          </ClearAllButton>
        </InfoRow>
      )}

      <TagsContainer>
        {tags.length === 0 ?
          <EmptyState>Nenhuma tag adicionada</EmptyState>
        : tags.map((tag) => (
            <TagItem key={tag.id}>
              <TagText title={tag.text}>{tag.text}</TagText>
              {!disabled && (
                <RemoveButton type="button" onClick={() => removeTag(tag.id)} title="Remover tag">
                  <Icon value={Close} size={10} color={Tokens.ColorTextPrimary} />
                </RemoveButton>
              )}
            </TagItem>
          ))
        }
      </TagsContainer>
    </Container>
  )
}
